"use client";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import useMediaQuery from "@/hooks/useMediaQuery";
import { useSearchPackagesList } from "@/hooks/useSearchPackagesList";
import { DialogTitle } from "@radix-ui/react-dialog";
import { Search } from "lucide-react";
import { useTranslations } from "next-intl";
import React, { forwardRef, useEffect, useState } from "react";
import CountryRegionSearch from "./CountryRegionSearch";

function SearchPackagesList({
  dialogTrigger,
  isDataVoice,
  topDestinations,
}: {
  dialogTrigger?: React.ReactNode;
  isDataVoice?: boolean;
  topDestinations: React.ReactNode;
}) {
  const [showDialog, setShowDialog] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const t = useTranslations("Shared.countrySearch");
  const [shouldPrefetchPackages, setShouldPrefetchPackages] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setShouldPrefetchPackages(true);
    }, 30_000);

    return () => window.clearTimeout(timer);
  }, []);

  const { data: searchPackagesList, isLoading: isListLoading } =
    useSearchPackagesList(showDialog || shouldPrefetchPackages);

  return isDesktop ? (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogTrigger asChild>
        {dialogTrigger ? (
          dialogTrigger
        ) : (
          <PlaceHolderInputField inputPlaceholder={t("inputPlaceholder")} />
        )}
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-montserrat text-lg font-semibold">
            {t(`inputPlaceholder`)}
          </DialogTitle>
        </DialogHeader>

        <CountryRegionSearch
          packagesList={searchPackagesList ?? null}
          topDestinations={topDestinations}
          isSearchLoading={isListLoading}
          setShowDialog={setShowDialog}
          isDataVoice={isDataVoice}
        />
      </DialogContent>
    </Dialog>
  ) : (
    <Drawer
      repositionInputs={true}
      open={showDialog}
      onOpenChange={setShowDialog}
    >
      <DrawerTrigger asChild>
        {dialogTrigger ? (
          dialogTrigger
        ) : (
          <PlaceHolderInputField inputPlaceholder={t("inputPlaceholder")} />
        )}
      </DrawerTrigger>
      <DrawerContent className="p-3">
        <DrawerHeader className="p-0">
          <DrawerTitle className="mb-2 text-start font-montserrat text-lg font-semibold">
            {t(`inputPlaceholder`)}
          </DrawerTitle>
        </DrawerHeader>

        <CountryRegionSearch
          packagesList={searchPackagesList ?? null}
          topDestinations={topDestinations}
          isSearchLoading={isListLoading}
          setShowDialog={setShowDialog}
          isDataVoice={isDataVoice}
        />
      </DrawerContent>
    </Drawer>
  );
}

export default SearchPackagesList;

const PlaceHolderInputField = forwardRef<
  HTMLInputElement,
  { inputPlaceholder: string } & React.HTMLAttributes<HTMLDivElement>
>(({ inputPlaceholder, onClick, ...rest }, ref) => {
  const triggerProps = rest as Record<string, unknown>;
  delete triggerProps.type;
  return (
    <div
      role="button"
      aria-label={inputPlaceholder}
      className="relative z-20 w-full cursor-pointer"
    >
      <Input
        ref={ref}
        role="combobox"
        placeholder={inputPlaceholder}
        className="w-full cursor-pointer rounded-full shadow"
        readOnly
        {...(triggerProps as React.InputHTMLAttributes<HTMLInputElement>)}
        onClick={
          onClick as unknown as React.MouseEventHandler<HTMLInputElement>
        }
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            (onClick as unknown as (e: React.SyntheticEvent) => void)?.(e);
          }
        }}
      />
      <Search className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 transform text-primary" />
    </div>
  );
});
PlaceHolderInputField.displayName = "PlaceHolderInputField";
